import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { foodItemAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  ChefHat,
  Edit,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';
import SellerLayout from '../../components/layouts/SellerLayout';

const FOOD_PLACEHOLDER = "https://images.unsplash.com/photo-1672477179695-7276b0602fa9?w=200";

export default function SellerItems() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const basePath = user?.role === 'dealer' ? '/dealer' : '/seller';

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const response = await foodItemAPI.getAll({ seller_id: user.id });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    
    try {
      await foodItemAPI.delete(id);
      setItems(items.filter(i => i.id !== id));
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <SellerLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">My Items</h1>
            <p className="text-muted-foreground">{items.length} items</p>
          </div>
          <Button 
            onClick={() => navigate(`${basePath}/items/new`)}
            className="rounded-full"
            data-testid="add-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>

        {/* Items Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <Skeleton className="h-40 w-full" />
                <CardContent className="p-4">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state py-20">
            <ChefHat className="empty-state-icon" />
            <h3 className="font-heading text-xl font-semibold mb-2">No items yet</h3>
            <p className="text-muted-foreground mb-4">Start adding your delicious offerings</p>
            <Button onClick={() => navigate(`${basePath}/items/new`)} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Add First Item
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <Card key={item.id} className="overflow-hidden" data-testid={`item-${item.id}`}>
                <div className="relative h-40">
                  <img
                    src={item.image_url || FOOD_PLACEHOLDER}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 flex gap-1">
                    <span className={item.is_vegetarian ? 'veg-badge' : 'non-veg-badge'}></span>
                  </div>
                  {item.is_free && (
                    <span className="absolute top-2 right-2 free-badge">FREE</span>
                  )}
                  {!item.is_available && (
                    <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                      <Badge variant="secondary">Unavailable</Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-semibold truncate flex-1">{item.name}</h3>
                    <span className="price-tag ml-2">
                      {item.is_free ? 'Free' : `₹${item.price}`}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{item.category}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline">Qty: {item.quantity}</Badge>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`${basePath}/items/${item.id}/edit`)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(item.id, item.name)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </SellerLayout>
  );
}
